import { useAuth0 } from '@auth0/auth0-react';
import { UploadStatus } from '../components/fileUploader/types.ts';
import {
    formAndUploadFile,
    getSignedUrlForUpload,
    sendUploadResult,
} from '../services/fileService.ts';
import { FieldSize, ObjectIdString } from '../types/common.ts';
export type FileWithIdAndStatus = File & {
    uploadStatus: UploadStatus;
    _id: ObjectIdString | null;
};
export const MAX_FILE_NAME_LENGTH = FieldSize.S;

// The uploader repairs missing MIME types up front (see fileSignature.ts), but a
// file reaching this point without one must still not go out as fileType: '' —
// the API answers that with a 500 (PT-7025).
const FALLBACK_FILE_TYPE = 'application/octet-stream';

function fileToAfterUploadFile(
    file: File,
    fileId: ObjectIdString,
    uploadStatus: UploadStatus,
): FileWithIdAndStatus {
    // we need to keep the object reference for URL.createObjectURL method
    const fileWithIdAndStatus = file as FileWithIdAndStatus;

    fileWithIdAndStatus.uploadStatus = uploadStatus;
    fileWithIdAndStatus._id = fileId;
    return fileWithIdAndStatus;
}

const truncateFileName = (fileName: string, maxLength: number): string => {
    if (fileName.length <= maxLength) return fileName;
    const extIndex = fileName.lastIndexOf('.');
    const ext = extIndex > 0 ? fileName.slice(extIndex) : '';
    const name = fileName.slice(0, extIndex > 0 ? extIndex : fileName.length);
    return name.slice(0, maxLength - ext.length) + ext;
};

export const useFile = () => {
    const { getAccessTokenSilently } = useAuth0();

    const uploadFile = async (
        file: File,
        projectId: ObjectIdString,
        issueId: ObjectIdString,
    ): Promise<FileWithIdAndStatus> => {
        const fileType = file.type || FALLBACK_FILE_TYPE;
        const fileDto = {
            fileName: file.name,
            fileType,
            description: ' ',
            title: truncateFileName(file.name, MAX_FILE_NAME_LENGTH),
        };

        const {
            signedRequest: { url: signedUrl, fields: awsFields },
            document: { _id: fileId },
        } = await getSignedUrlForUpload(await getAccessTokenSilently(), {
            projectId,
            issueId,
            fileInfo: fileDto,
        });

        const reader = new FileReader();

        return new Promise<FileWithIdAndStatus>((resolve, reject) => {
            let uploadStatus = UploadStatus.pending;

            reader.onload = async () => {
                const arrayBuffer = reader.result as ArrayBuffer;
                const blob = new Blob([arrayBuffer], { type: fileType });

                try {
                    await formAndUploadFile(blob, awsFields, signedUrl);
                    uploadStatus = UploadStatus.success;
                } catch (e) {
                    console.error(e);
                    uploadStatus = UploadStatus.failed;
                }

                try {
                    await sendUploadResult(await getAccessTokenSilently(), {
                        projectId,
                        issueId,
                        documentId: fileId,
                        uploadStatus,
                    });
                } catch (e) {
                    console.error('Failed to send upload result:', e);
                    uploadStatus = UploadStatus.failed;
                    return reject(e);
                }

                resolve(fileToAfterUploadFile(file, fileId, uploadStatus));
            };

            reader.onerror = (error) => {
                console.error('FileReader error:', error);
                reject(error);
            };

            reader.readAsArrayBuffer(file);
        });
    };

    return { uploadFile };
};
